import type { MedplumClient } from '@medplum/core';
import type { Bundle, Observation, Provenance } from '@medplum/fhirtypes';

import {
  type FhirConnectionState,
  type FhirQueryState,
  useFhirConnection,
  useFhirData,
} from '@eluve/fhir-data';
import { flattenFhirGraphQLData } from '@eluve/fhir-renderer';
import { type ResultOf } from '@eluve/graphql-fhir';

import {
  REVIEW_STATUS_ATTESTED,
  REVIEW_STATUS_REJECTED,
  isPendingReview,
  setReviewStatusTag,
} from '../review-markers';

import {
  FHIR_PATIENT_ID_QUERY,
  PATIENT_OBSERVATIONS_QUERY,
} from './fhirQueries';

/**
 * Data layer for the patient-profile Observations card: reads via the FHIR
 * GraphQL connection, review writes via the Medplum SDK client. The two
 * paths share no cache — callers refetch the read after a write lands.
 */

function mapPatientId(
  data: ResultOf<typeof FHIR_PATIENT_ID_QUERY>,
): string | null {
  return data.PatientList?.[0]?.id ?? null;
}

/** Resolve the FHIR Patient id for an Eluve (Hasura) patient id. */
export function useFhirPatientId(
  patientId: string,
): FhirQueryState<string | null> {
  return useFhirData(FHIR_PATIENT_ID_QUERY, mapPatientId, {
    variables: { identifier: patientId },
  });
}

function mapObservations(
  data: ResultOf<typeof PATIENT_OBSERVATIONS_QUERY>,
): Observation[] {
  return flattenFhirGraphQLData(data).filter(
    (resource): resource is Observation & { id: string } =>
      resource.resourceType === 'Observation' && Boolean(resource.id),
  );
}

/** The patient's observations, paged newest-first. Skipped until the FHIR id resolves. */
export function usePatientObservations(
  fhirPatientId: string | null,
): FhirConnectionState<Observation[]> {
  return useFhirConnection(
    PATIENT_OBSERVATIONS_QUERY,
    'ObservationConnection',
    mapObservations,
    {
      variables: { patientRef: `Patient/${fhirPatientId}` },
      skip: !fhirPatientId,
    },
  );
}

export type ReviewDecision =
  | { kind: 'accept'; amendedValue?: number }
  | { kind: 'dismiss' };

/**
 * Apply a reviewer's decision to a pending AI-drafted observation —
 * the same transition the backend review capability performs
 * (`clinical-findings/review.ts`): re-read, verify still pending, flip the
 * native status + review-status tag, and record an attestation Provenance,
 * all in one transaction so the audit trail can't drift from the decision.
 *
 * - accept → status `final`, tag `attested`
 * - accept with a corrected value → value replaced, status `amended`
 *   (the AI's original stays in `_history`), tag `attested`
 * - dismiss → status `entered-in-error`, tag `rejected`
 */
export async function reviewObservation(
  client: MedplumClient,
  args: {
    observationId: string;
    decision: ReviewDecision;
    /** `Practitioner/<id>` recorded as the verifier on the Provenance. */
    reviewerReference: string | null;
  },
): Promise<void> {
  const { observationId, decision, reviewerReference } = args;
  const current = await client.readResource('Observation', observationId);
  if (!isPendingReview(current)) {
    throw new Error('This value is no longer awaiting review.');
  }

  let next: Observation;
  if (decision.kind === 'dismiss') {
    next = setReviewStatusTag(
      { ...current, status: 'entered-in-error' },
      REVIEW_STATUS_REJECTED,
    );
  } else if (decision.amendedValue !== undefined) {
    next = setReviewStatusTag(
      {
        ...current,
        status: 'amended',
        valueQuantity: {
          ...current.valueQuantity,
          value: decision.amendedValue,
        },
      },
      REVIEW_STATUS_ATTESTED,
    );
  } else {
    next = setReviewStatusTag(
      { ...current, status: 'final' },
      REVIEW_STATUS_ATTESTED,
    );
  }

  // ifMatch pins the write to the version the pending check saw — a
  // concurrent reviewer's write bumps the version and this one is rejected
  // instead of silently overwriting their decision.
  const entries: NonNullable<Bundle['entry']> = [
    {
      request: {
        method: 'PUT',
        url: `Observation/${observationId}`,
        ...(current.meta?.versionId
          ? { ifMatch: `W/"${current.meta.versionId}"` }
          : {}),
      },
      resource: next,
    },
  ];
  if (decision.kind === 'accept' && reviewerReference) {
    entries.push({
      request: { method: 'POST', url: 'Provenance' },
      resource: buildAttestationProvenance(
        `Observation/${observationId}`,
        reviewerReference,
      ),
    });
  }

  const response = await client.executeBatch({
    resourceType: 'Bundle',
    type: 'transaction',
    entry: entries,
  });
  assertNoFailedEntries(response);
}

const DATA_OPERATION_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-DataOperation';
const PROVENANCE_PARTICIPANT_TYPE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/provenance-participant-type';

/** Attestation Provenance: the reviewer verified this finding. */
function buildAttestationProvenance(
  target: string,
  reviewerReference: string,
): Provenance {
  return {
    resourceType: 'Provenance',
    target: [{ reference: target }],
    recorded: new Date().toISOString(),
    activity: {
      coding: [
        { system: DATA_OPERATION_SYSTEM, code: 'UPDATE', display: 'revise' },
      ],
    },
    agent: [
      {
        type: {
          coding: [
            { system: PROVENANCE_PARTICIPANT_TYPE_SYSTEM, code: 'verifier' },
          ],
        },
        who: { reference: reviewerReference },
      },
    ],
  };
}

function assertNoFailedEntries(response: Bundle): void {
  const statuses = (response.entry ?? [])
    .map((entry) => Number.parseInt(entry.response?.status ?? '', 10))
    .filter((code) => !(code >= 200 && code < 300));
  if (statuses.length === 0) return;
  // 409/412 is the ifMatch guard firing: someone else reviewed this first.
  if (statuses.some((code) => code === 409 || code === 412)) {
    throw new Error('This value has already been reviewed by someone else.');
  }
  throw new Error(`Review write failed: ${statuses.join(', ')}`);
}
